import { Painter } from "./Painter";

/********************************************************************
 * Set theme interface for control.
 *******************************************************************/
export interface ITheme{
    setTheme(theme: Theme): void;
}

/********************************************************************
 * 定义 ThemeOptions 接口
 *******************************************************************/
export interface ThemeOptions {
    name: string;
    color: {
        primary: number;
        secondary: number;
        contrast: number;

        success: number;
        info: number;
        warning: number;
        danger: number;

        bg: number;
        control: number;
        //bgDark: number;
        //bgLight: number;

        text: number;          // 文本色（用于普通背景下的文字）
        textContrast: number,  // 文本对比色（可用于对比色背景下的文字）
    };
    text: {
        normal: { font: string; size: number; fontWeight: string };
        small: { font: string; size: number; fontWeight: string };
        h1: { font: string; size: number; fontWeight: string };
        h2: { font: string; size: number; fontWeight: string };
        h3: { font: string; size: number; fontWeight: string };
        h4: { font: string; size: number; fontWeight: string };
        h5: { font: string; size: number; fontWeight: string };
    };
    border: {
        color: number;
        width: number;
        radius: number;
    };
    control: {
        width: number;
        height: number;
        rowHeight: number;
    };
    link: {
        color: number;
        hoverColor: number;
        visitedColor: number;
    };
}

/************************************************************
 * Theme
 ***********************************************************/
export class Theme implements ThemeOptions{
    // named color
    static namedColor = {
        blue: 0x0000ff,
        green: 0x00ff00,
        red: 0xff0000,
        yellow: 0xffff00,
        black: 0x000000,
        white: 0xffffff,
        gray: 0x808080,
        purple: 0x800080,
        pink: 0xff00ff,
        orange: 0xffa500,
        brown: 0xff00ff,
    }

    // name
    name  = 'iOSLight';

    // color
    color = {
        primary: 0x007bff,   // 主色
        secondary: 0x7633d4, // 辅色
        contrast: 0xffa500,  // 对比色

        success: 0x28a745,
        info: 0x17a2b8,
        warning: 0xffc107,
        danger: 0xdc3545,

        bg: 0xffffff,
        control: 0xffffff,
        text : 0x000000,         // 文本色（用于普通背景下的文字）
        textContrast: 0xf8f9fa,  // 文本对比色（可用于对比色背景下的文字）
    }

    // text
    text = {
        normal: { font: 'Arial', size: 16, fontWeight: 'normal' },
        small:  { font: 'Arial', size: 12, fontWeight: 'normal' },
        h1: { font: 'Arial', size: 24, fontWeight: 'bold'},
        h2: { font: 'Arial', size: 22, fontWeight: 'bold'},
        h3: { font: 'Arial', size: 20, fontWeight: 'bold'},
        h4: { font: 'Arial', size: 18, fontWeight: 'bold'},
        h5: { font: 'Arial', size: 16, fontWeight: 'bold'},
    }

    // border
    border = {
        color: 0xcdcdcd,
        width: 1,
        radius: 8,
    }

    // size
    control = {
        width: 120,
        height: 40,
        rowHeight: 48,
    }

    // link
    link = {
        color: 0x0000ff,
        hoverColor: 0xffa500, //0x1010ff,
        visitedColor: 0xffa500, //0xa0a0a0,
    }
    
    // 修改构造函数
    constructor(opt: ThemeOptions) {
        Object.assign(this, opt);
    }

    //---------------------------------------------------
    // inner themes
    //---------------------------------------------------
    /** Theme light */
    public static themeLight = new Theme({
        name: 'Light',
        color: {
            primary: 0x007bff,
            secondary: 0x7633d4,
            contrast: 0xffa500,  // 对比色

            success: 0x28a745,
            info: 0x17a2b8,
            warning: 0xffc107,
            danger: 0xdc3545,

            bg: 0xffffff,
            control: 0xffffff,
            text: 0x000000,
            textContrast: 0xf8f9fa,
        },
        text: {
            normal: { font: 'Arial', size: 16, fontWeight: 'normal' },
            small: { font: 'Arial', size: 12, fontWeight: 'normal' },
            h1: { font: 'Arial', size: 24, fontWeight: 'bold' },
            h2: { font: 'Arial', size: 22, fontWeight: 'bold' },
            h3: { font: 'Arial', size: 20, fontWeight: 'bold' },
            h4: { font: 'Arial', size: 18, fontWeight: 'bold' },
            h5: { font: 'Arial', size: 16, fontWeight: 'bold' }
        },
        border: {
            color: 0xcdcdcd,
            width: 1,
            radius: 8
        },
        control: {
            width: 120,
            height: 40,
            rowHeight: 48
        },
        link: {
            color: 0x0000ff,
            hoverColor: 0xffa500, //0x1010ff,
            visitedColor: 0xa0a0a0,
        }
    });

    /** Theme dark */
    public static themeDark = new Theme({
        name: 'Dark',
        color: {
            primary: 0x007bff,
            secondary: 0x7633d4,
            contrast: 0xffa500,  // 对比色

            success: 0x28a745,
            info: 0x17a2b8,
            warning: 0xffc107,
            danger: 0xdc3545,

            bg: 0x171717,
            control: 0x343a40,
            text: 0xcccccc,
            textContrast: 0x333333,
        },
        text: {
            normal: { font: 'Arial', size: 16, fontWeight: 'normal' },
            small: { font: 'Arial', size: 12, fontWeight: 'normal' },
            h1: { font: 'Arial', size: 24, fontWeight: 'bold' },
            h2: { font: 'Arial', size: 22, fontWeight: 'bold' },
            h3: { font: 'Arial', size: 20, fontWeight: 'bold' },
            h4: { font: 'Arial', size: 18, fontWeight: 'bold' },
            h5: { font: 'Arial', size: 16, fontWeight: 'bold' }
        },
        border: {
            color: 0x707070,
            width: 1,
            radius: 8
        },
        control: {
            width: 120,
            height: 40,
            rowHeight: 48
        },
        link: {
            color: 0xff0000,
            hoverColor: 0x00ff00,
            visitedColor: 0xa0a0a0
        }
    });

    /** Theme green */
    public static themeGreen = new Theme({
        name: 'Green',
        color: {
            primary: 0x2ecc71,
            secondary: 0x27ae60,
            contrast: 0xffa500,  // 对比色

            success: 0x28a745,
            info: 0x17a2b8,
            warning: 0xf1c40f,
            danger: 0xe74c3c,

            bg: 0xecf0f1,
            control: 0xdfe4ea,
            text: 0x2c3e50,
            textContrast: 0xffffff,
        },
        text: {
            normal: { font: 'Arial', size: 16, fontWeight: 'normal' },
            small: { font: 'Arial', size: 12, fontWeight: 'normal' },
            h1: { font: 'Arial', size: 24, fontWeight: 'bold' },
            h2: { font: 'Arial', size: 22, fontWeight: 'bold' },
            h3: { font: 'Arial', size: 20, fontWeight: 'bold' },
            h4: { font: 'Arial', size: 18, fontWeight: 'bold' },
            h5: { font: 'Arial', size: 16, fontWeight: 'bold' }
        },
        border: {
            color: 0x95a5a6,
            width: 1,
            radius: 8
        },
        control: {
            width: 120,
            height: 40,
            rowHeight: 48
        },
        link: {
            color: 0x27ae60,
            hoverColor: 0x2ecc71,
            visitedColor: 0x95a5a6
        }
    });

    /** Theme purple */
    public static themePurple = new Theme({
        name: 'Purple',
        color: {
            primary: 0x9b59b6,
            secondary: 0x8e44ad,
            contrast: 0xffa500,  // 对比色

            success: 0x28a745,
            info: 0x17a2b8,
            warning: 0xf1c40f,
            danger: 0xe74c3c,

            bg: 0xf3e5f5,
            control: 0xe1bee7,
            text: 0x4a148c,
            textContrast: 0xf8bbd0,
        },
        text: {
            normal: { font: 'Arial', size: 16, fontWeight: 'normal' },
            small: { font: 'Arial', size: 12, fontWeight: 'normal' },
            h1: { font: 'Arial', size: 24, fontWeight: 'bold' },
            h2: { font: 'Arial', size: 22, fontWeight: 'bold' },
            h3: { font: 'Arial', size: 20, fontWeight: 'bold' },
            h4: { font: 'Arial', size: 18, fontWeight: 'bold' },
            h5: { font: 'Arial', size: 16, fontWeight: 'bold' }
        },
        border: {
            color: 0xce93d8,
            width: 1,
            radius: 8
        },
        control: {
            width: 120,
            height: 40,
            rowHeight: 48
        },
        link: {
            color: 0x8e44ad,
            hoverColor: 0x9b59b6,
            visitedColor: 0xce93d8
        }
    });

    /** Theme darkblue */
    public static themeDarkBlue = new Theme({
        name: 'DarkBlue',
        color: {
            primary: 0x3498db,
            secondary: 0x2980b9,
            contrast: 0xffa500,  // 对比色

            success: 0x28a745,
            info: 0x17a2b8,
            warning: 0xf1c40f,
            danger: 0xe74c3c,

            bg: 0xe3f2fd,
            control: 0xc5e1a5,
            text: 0x0d47a1,
            textContrast: 0x81d4fa,
        },
        text: {
            normal: { font: 'Arial', size: 16, fontWeight: 'normal' },
            small: { font: 'Arial', size: 12, fontWeight: 'normal' },
            h1: { font: 'Arial', size: 24, fontWeight: 'bold' },
            h2: { font: 'Arial', size: 22, fontWeight: 'bold' },
            h3: { font: 'Arial', size: 20, fontWeight: 'bold' },
            h4: { font: 'Arial', size: 18, fontWeight: 'bold' },
            h5: { font: 'Arial', size: 16, fontWeight: 'bold' }
        },
        control: {
            width: 120,
            height: 40,
            rowHeight: 48
        },
        border: {
            color: 0x90caf9,
            width: 1,
            radius: 8
        },
        link: {
            color: 0x2980b9,
            hoverColor: 0x3498db,
            visitedColor: 0x90caf9
        }
    });

    //-------------------------------------------------------       
    // Theme change
    //-------------------------------------------------------       
    /** Global Theme*/
    public static current = Theme.themeLight;

    /** Set page theme.*/
    public static setTheme(scene: Phaser.Scene, theme: Theme | null = null){
        if (theme != null)
            this.current = theme;

        // bg,controls
        scene.cameras.main.setBackgroundColor(this.current.color.bg);
        scene.children.list.forEach(ele => {
            if ((ele as any).setTheme != undefined)
              (ele as any).setTheme(this.current);
        });
    }

    //-------------------------------------------------------       
    // Auto layout
    //-------------------------------------------------------       


}